
import React, { useState, useEffect } from "react";
import { Mail, ArrowLeft, AlertCircle, CheckCircle } from "lucide-react";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/components/auth/AuthProvider";

export default function EmailVerificationPage() {
  const [isResending, setIsResending] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [verificationChecked, setVerificationChecked] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Get the user's email from the session on component mount or from navigation state
  useEffect(() => {
    const getEmailInfo = async () => {
      // First try to get email from location state (passed during registration)
      const stateEmail = location.state?.email;
      if (stateEmail) {
        setUserEmail(stateEmail);
        return;
      }
      
      // If not available, get from session
      const { data } = await supabase.auth.getSession();
      if (data.session?.user?.email) {
        setUserEmail(data.session.user.email);
      }
    };
    
    getEmailInfo();
  }, [location]);

  // If user is authenticated, redirect to dashboard
  useEffect(() => {
    // Only redirect if we've established the user is verified
    if (user && !verificationChecked) {
      setVerificationChecked(true);
      
      // Small delay to allow for state updates and UI transition
      const timer = setTimeout(() => {
        toast.success("Email verified successfully", {
          description: "Welcome to your dashboard"
        });
        navigate('/dashboard');
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [user, navigate, verificationChecked]);
  
  const handleResendEmail = async () => {
    try {
      if (!userEmail) {
        toast.error("No email found", {
          description: "Please try logging in again"
        });
        return;
      }
      
      setIsResending(true);
      
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: userEmail
      });
      
      if (error) throw error;
      
      toast.success("Verification email resent successfully", {
        description: "Please check your inbox or spam folder"
      });
    } catch (error: any) {
      toast.error("Failed to resend verification email", {
        description: error.message || "Please try again later"
      });
    } finally {
      setIsResending(false);
    }
  };

  if (user) {
    return (
      <Layout hideFooter>
        <Helmet>
          <title>Email Verified | Grandpal</title>
        </Helmet>
        
        <div className="min-h-[80vh] flex items-center justify-center">
          <div className="max-w-md w-full mx-4 text-center">
            <div className="mb-6 flex justify-center">
              <div className="rounded-full bg-green-100 p-3">
                <CheckCircle className="h-12 w-12 text-green-600" />
              </div>
            </div>
            
            <h1 className="text-2xl md:text-3xl font-semibold mb-4">
              Email Verified!
            </h1>
            
            <p className="text-gray-600 mb-8">
              Redirecting you to your dashboard...
            </p>
            
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-grandpal-green" />
            </div>
          </div>
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout hideFooter>
      <Helmet>
        <title>Verify Your Email | Grandpal</title>
      </Helmet>
      
      <div className="min-h-[80vh] flex items-center justify-center">
        <div className="max-w-md w-full mx-4 text-center">
          <div className="mb-6 flex justify-center">
            <div className="rounded-full bg-grandpal-green/10 p-3">
              <Mail className="h-12 w-12 text-grandpal-green" />
            </div>
          </div>
          
          <h1 className="text-2xl md:text-3xl font-semibold mb-4">
            Verify Your Email to Continue
          </h1>
          
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start text-left">
            <AlertCircle className="h-5 w-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
            <p className="text-amber-800 text-sm">
              For security reasons, we need to verify your email address before you can access your account.
              Please check your inbox and click the verification link we sent you.
            </p>
          </div>
          
          <p className="text-gray-600 mb-8 text-left">
            We've sent a verification email to:
            {userEmail && (
              <span className="block mt-2 font-medium text-black text-lg">{userEmail}</span>
            )}
          </p>
          
          <div className="space-y-4">
            <Button 
              onClick={handleResendEmail} 
              variant="default" 
              className="w-full bg-grandpal-green hover:bg-grandpal-green/90"
              disabled={isResending || !userEmail}
            >
              {isResending ? "Resending..." : "Resend Verification Email"}
            </Button>
            
            <Button asChild variant="outline" className="w-full">
              <Link to="/auth/login">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Return to Sign In
              </Link>
            </Button>
            
            <div className="mt-8 text-sm text-gray-500 p-4 bg-gray-50 rounded-md">
              <h3 className="font-medium mb-2">If you don't see the email:</h3>
              <ul className="list-disc list-inside space-y-1 text-left">
                <li>Check your spam or junk folder</li>
                <li>Ensure you entered your email correctly</li>
                <li>Add noreply@grandpal.com to your contacts</li>
                <li>Try signing in - if your account is already verified, you'll be logged in</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
